import {
  Box,
  Button,
  Card,
  Container,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { GetApp, InfoOutlined, Save } from '@mui/icons-material';
import { makeStyles } from '@mui/styles';
import { FC, useMemo, useState } from 'react';
import { Loader, Page } from '../../../components';
import { FileUploader } from '../../../components/file/file-uploader';
import { createActiveInvestmentSales } from '../../../fetch/imports';
import { useToastContext } from '../../../hooks';
import { theme } from '../../../styles';

const templateUrl = `${process.env.PUBLIC_URL}/templates/IVS_Active_Activity_Import_Template.xlsx`;

const documentationUrl = `${process.env.PUBLIC_URL}/templates/IVS_Active_Bulk_Import_Documentation.docx`;

interface IImportResults {
  successCount: number;
  errors: string[];
  validationErrors: string[];
}

export const ImportActiveInvestmentSales: FC = () => {
  const { showToast } = useToastContext();
  const [importName, setImportName] = useState('');
  const [uploadedFile, setUploadedFile] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [results, setResults] = useState<IImportResults | null>(null);

  const classes = useStyles();

  // The importer reports one line per row it could not import. Skipped rows read
  // "Row n skipped: ...", failures read "Row n failed: ...", and duplicate skips are the ones whose
  // reason is that a matching activity already exists.
  const summary = useMemo(() => {
    if (!results) return null;

    const issues = [...(results.validationErrors ?? []), ...(results.errors ?? [])];
    const skipped = issues.filter(x => / skipped:/i.test(x));
    const failed = issues.filter(x => / failed to import:| failed:/i.test(x));
    const duplicates = skipped.filter(x => /already exists/i.test(x));

    return {
      imported: results.successCount ?? 0,
      processed: (results.successCount ?? 0) + issues.length,
      skipped: skipped.length,
      duplicates: duplicates.length,
      failed: failed.length,
      issues,
    };
  }, [results]);

  const handleSubmit = async () => {
    if (!uploadedFile || !importName.trim()) return;

    setIsSubmitting(true);
    setResults(null);

    try {
      const res = await createActiveInvestmentSales(importName, uploadedFile);

      if (res?.Detail) {
        showToast('error', `Error, ${res.Detail}`);
        setResults({ successCount: 0, errors: res.Detail.split('\r\n'), validationErrors: [] });
        return;
      }

      setResults({
        successCount: res?.successCount ?? 0,
        errors: res?.errors ?? [],
        validationErrors: res?.validationErrors ?? [],
      });
    } catch {
      showToast('error', 'Error importing document, please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Page title="Import Active Investment Sales Activities">
      <Container maxWidth={false} disableGutters>
        <Card className={classes.uploader} elevation={2}>
          <Box marginBottom="1.5rem" display="flex" alignItems="center" style={{ gap: '0.25rem' }}>
            <Button
              component="a"
              href={templateUrl}
              download
              startIcon={<GetApp />}
              color="primary"
              variant="outlined"
              size="small"
            >
              Download Template
            </Button>
            <Tooltip title="Download documentation (Word)">
              <IconButton
                component="a"
                href={documentationUrl}
                download
                size="small"
                aria-label="Download bulk import documentation"
              >
                <InfoOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <Typography variant="caption" component="p" color="textSecondary" gutterBottom>
            Creates activities in Lead, Proposal, Pending Active, Active, Accepted Offer, Under
            Contract or Hard Money. Later-stage fields can be left blank - complete them by opening
            the activity in Frankie. Historical deals use the Investment Sales Activities import.
          </Typography>

          <Grid container>
            <Grid container item spacing={2} xs={12} sm={6}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  variant="outlined"
                  autoComplete="nope"
                  label="Import Name"
                  placeholder="Enter an import name..."
                  name="importName"
                  size="small"
                  required
                  value={importName}
                  onChange={e => setImportName(e.target.value)}
                  inputProps={{ maxLength: 300 }}
                />
              </Grid>
              <Grid item xs={12}>
                <FileUploader
                  handleFile={val => setUploadedFile(val)}
                  handleRemove={() => setUploadedFile(null)}
                  removeText="Remove"
                  templateFile={uploadedFile?.name ?? ''}
                  isRequired
                  dropzoneText="File to Import (.xlsx)"
                />
              </Grid>
            </Grid>
          </Grid>

          <Box marginTop="2rem">
            <Button
              disabled={!uploadedFile || !importName.trim() || isSubmitting}
              onClick={handleSubmit}
              startIcon={<Save />}
              color="secondary"
              variant="contained"
            >
              Upload
            </Button>
          </Box>

          {summary && (
            <Box marginTop="2rem" maxWidth="55rem">
              <Typography variant="subtitle1" gutterBottom>
                Import Results
              </Typography>
              <Typography variant="body2" component="div">
                <div>{`Rows processed: ${summary.processed}`}</div>
                <div>{`Rows imported: ${summary.imported}`}</div>
                <div>{`Rows skipped: ${summary.skipped}`}</div>
                <div>{`Duplicate records: ${summary.duplicates}`}</div>
                <div>{`Validation failures: ${summary.failed}`}</div>
              </Typography>

              {summary.issues.length > 0 && (
                <Box marginTop="1rem" className={classes.issues}>
                  {summary.issues.map((issue, i) => (
                    <Typography variant="caption" component="div" key={i}>
                      {issue}
                    </Typography>
                  ))}
                </Box>
              )}

              <Box marginTop="1rem">
                <Button
                  color="primary"
                  variant="text"
                  size="small"
                  onClick={() => {
                    setResults(null);
                    setUploadedFile(null);
                    setImportName('');
                  }}
                >
                  Start Another Import
                </Button>
              </Box>
            </Box>
          )}

          {isSubmitting && (
            <Loader position="centered" type="fullscreen">
              Importing Active Investment Sales Activities ...
            </Loader>
          )}
        </Card>
      </Container>
    </Page>
  );
};

const useStyles = makeStyles(() => ({
  uploader: {
    padding: theme.spacing(2),
  },
  issues: {
    maxHeight: '20rem',
    overflowY: 'auto',
    padding: theme.spacing(1),
    border: '1px solid rgba(0, 0, 0, 0.12)',
    borderRadius: 4,
  },
}));
